import React, { useEffect, useCallback } from "react";
import {
  Box,
  Chip,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Checkbox,
  ListItem,
  Typography,
} from "@material-ui/core";
import { useBmapi } from "../../utils/bmapi-context";
import { useIntl } from "react-intl";
import { getErrorMessageString } from "../../utils/errors";

export default function GenListDropdown(props) {
  const {
    listId,
    lang,
    emptyItem,
    value: pvalue,
    name,
    multiple,
    onChange,
    required,
    margin,
    label,
    itemTypes,
    businessId,
    refreshKey,
    sortByLabel = false,
  } = props || {};

  const [listItems, setListItems] = React.useState([]);
  const [filteredItems, setFilteredItems] = React.useState([]);
  const [filterType, setFilterType] = React.useState(
    itemTypes != null && Array.isArray(itemTypes) ? itemTypes[0]?.id : null
  );
  const [langIndex, setLangIndex] = React.useState(-1);

  const { bmapi, notifyError } = useBmapi();
  const intl = useIntl();

  // normalizza value se multiple
  const value = multiple && (!pvalue || !Array.isArray(pvalue)) ? [] : pvalue;

  // funzione stabile per mostrare il testo a partire da langIndex
  const getDisplayValue = useCallback(
    (item) => {
      if (
        item &&
        Array.isArray(item.values) &&
        langIndex >= 0 &&
        item.values[langIndex]?.str_value !== undefined
      ) {
        return item.values[langIndex]?.str_value;
      }
      return item?.item_id ?? "";
    },
    [langIndex]
  );

  // FETCH DATI (fixato per non rifare la fetch quando cambia langIndex/getDisplayValue)
  useEffect(() => {
    let isMounted = true;

    const fetchItems = async () => {
      try {
        const genList = await bmapi.getGenListAdmin(listId, businessId);
        if (!isMounted) return;

        const apiItems = genList?.items || [];
        const allLangs = genList?.all_lang || [];
        const idx = allLangs.indexOf(lang);
        setLangIndex(idx);

        let items = apiItems;

        if (emptyItem) {
          items = [emptyItem, ...items];
        }

        if (sortByLabel) {
          // usiamo idx locale invece di getDisplayValue (per evitare dipendenza reattiva)
          const localGetDisplayValue = (item) => {
            if (
              item &&
              Array.isArray(item.values) &&
              idx >= 0 &&
              item.values[idx]?.str_value !== undefined
            ) {
              return item.values[idx]?.str_value;
            }
            return item?.item_id ?? "";
          };

          items = items.slice().sort((a, b) =>
            localGetDisplayValue(a)
              .toString()
              .localeCompare(localGetDisplayValue(b).toString(), undefined, {
                sensitivity: "base",
              })
          );
        }

        setListItems(items);
      } catch (e) {
        if (!isMounted) return;

        if (e.code === "NOT_FOUND") {
          setListItems([]);
        } else {
          notifyError(getErrorMessageString(e, intl));
        }
      }
    };

    fetchItems();

    return () => {
      isMounted = false;
    };
  }, [
    bmapi,
    listId,
    businessId,
    lang,
    emptyItem,
    intl,
    notifyError,
    sortByLabel,
    refreshKey,
    // getDisplayValue rimosso dalle deps
  ]);

  // filtro per tipo
  useEffect(() => {
    setFilteredItems(
      filterType
        ? listItems.filter((item) => item.type === filterType)
        : listItems
    );
  }, [listItems, filterType]);

  const ITEM_HEIGHT = 50;
  const ITEM_PADDING_TOP = 10;

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 300,
      },
    },
    getContentAnchorEl: null,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
  };

  const onChangeValue = (event, selectedValue) => {
    // evita che il click sui checkbox del filtro scateni la select
    if (event.target?.type === "checkbox") {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (onChange) {
      onChange(event, selectedValue);
    }
  };

  const handleDeleteChip = (chipValue) => {
    const newValue = value.filter((val) => val !== chipValue);
    onChange({ target: { value: newValue } });
  };

  return (
    <FormControl fullWidth required={required} margin={margin}>
      <InputLabel>{label || ""}</InputLabel>
      <Select
        name={name}
        multiple={multiple}
        value={value}
        onChange={onChangeValue}
        MenuProps={MenuProps}
        input={<Input />}
        renderValue={(selected) =>
          multiple ? (
            <Box style={{ display: "flex", flexWrap: "wrap" }}>
              {selected.map((sel) => {
                const selectedItem = listItems.find(
                  (item) => item.item_id === sel
                );

                const displayValue = selectedItem
                  ? getDisplayValue(selectedItem)
                  : sel;

                return (
                  <Chip
                    key={sel}
                    label={displayValue}
                    onDelete={() => handleDeleteChip(sel)}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                    }}
                  />
                );
              })}
            </Box>
          ) : (
            (() => {
              const selectedItem = listItems.find(
                (item) => item.item_id === selected
              );
              return selectedItem
                ? getDisplayValue(selectedItem)
                : selected || "";
            })()
          )
        }
      >
        {/* filtro tipo dentro al menu */}
        <ListItem disableGutters>
          {itemTypes?.map((type) => (
            <Box
              key={type.id}
              display="flex"
              alignItems="center"
              mr={2}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <Checkbox
                checked={filterType === type.id}
                onClick={(event) => {
                  event.stopPropagation();
                  setFilterType(filterType === type.id ? null : type.id);
                }}
                color="primary"
              />
              <Typography variant="body2">{type.label}</Typography>
            </Box>
          ))}
        </ListItem>

        {/* opzioni */}
        {filteredItems.map((option) => (
          <MenuItem key={option.item_id} value={option.item_id}>
            {getDisplayValue(option)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
